import React, { useState } from 'react';
import { getIn, useFormik } from 'formik';
import { ApiReadyWebsiteConfiguration, FooterCustomLink, WebsiteConfiguration } from '../types/websiteConfiguration';
import { ObjectError } from '../types/handlers';
import { Banner } from '../types/banners';
import { prepareForRender, prepareForApi, prepareForValidation } from '../utils/format';
import { getValidationSchema } from '../utils/validationSchema';

export const useForm = (
  initialValues: WebsiteConfiguration,
  onSubmit: (values: ApiReadyWebsiteConfiguration) => Promise<void>,
  validationSchema: ReturnType<typeof getValidationSchema>
) => {
  const [clickedSubmit, setClickedSubmit] = useState(false);

  const {
    setFieldValue,
    setTouched,
    validateField,
    setFieldTouched,
    validateForm,
    handleSubmit: formikSubmit,
    errors,
    touched,
    values,
    ...formik
  } = useFormik<WebsiteConfiguration>({
    initialValues: prepareForRender(initialValues),
    onSubmit: async (values) => {
      const apiReadyValues = prepareForApi(values);
      await onSubmit(apiReadyValues);
      setClickedSubmit(false);
    },
    validateOnChange: false,
    validateOnBlur: true,
    validationSchema,
  });

  const getError = (fieldName: string) => (touched[fieldName] || clickedSubmit ? getIn(errors, fieldName) : null);

  const createObjectFieldHandlers = (fieldName: keyof WebsiteConfiguration) => ({
    onBlur: () => setFieldTouched(fieldName, true, true),
    onChange: async (deepFieldName: string, value: string | boolean, shouldValidate?: boolean) => {
      const fullFieldName = [fieldName, deepFieldName].join('.');
      setFieldValue([fieldName, deepFieldName].join('.'), value, shouldValidate);
      if (!touched[fieldName]) setFieldTouched(fullFieldName, true);
    },
    getError: (deepFieldName: string): string | null =>
      touched[fieldName] || clickedSubmit ? getIn(errors, `${fieldName}.${deepFieldName}`) : null,
  });

  const createFieldHandlers = <V>(fieldName: string) => ({
    onBlur: () => setFieldTouched(fieldName, true, true),
    onChange: async (value: V, shouldValidate?: boolean) => {
      if (!touched[fieldName]) setFieldTouched(fieldName, true);
      await setFieldValue(fieldName, value, shouldValidate);
    },
    getError: (): string | null => (touched[fieldName] || clickedSubmit ? getIn(errors, fieldName) : null),
  });

  const createArrayFieldHandlers = <T>(fieldName: string) => ({
    onBlur: () => setFieldTouched(fieldName, true, true),
    onChange: async (value: Array<T>, shouldValidate?: boolean) => {
      setFieldTouched(fieldName, true);
      await setFieldValue(fieldName, value, shouldValidate);
    },
    getError: (): ObjectError<T> => (touched[fieldName] || clickedSubmit ? getIn(errors, fieldName) : null),
  });

  const statusHandlers = createFieldHandlers('enabled');
  const bannersHandlers = createArrayFieldHandlers<Banner>('banners');
  const footerCustomLinksHandlers = createArrayFieldHandlers<FooterCustomLink>('footer_custom_links');
  const homepageSectionsHandlers = createFieldHandlers('homepage_sections');

  const brandingHandlers = createObjectFieldHandlers('branding');
  const heroHandlers = createObjectFieldHandlers('hero');
  const faviconHandlers = createObjectFieldHandlers('favicon');
  const aboutUsHandlers = createObjectFieldHandlers('about_us');
  const contactUsHandlers = createObjectFieldHandlers('contact_us');
  const seoHandlers = createObjectFieldHandlers('meta');

  const handleSubmit = async () => {
    setClickedSubmit(true);

    try {
      await validateForm(prepareForValidation(values));
      formikSubmit();
    } catch (err) {
      alert(err);
    }
  };

  const hasVisibleErrors = Object.keys(touched).some((key) => errors[key]) || (clickedSubmit && !formik.isValid);
  const isFormTouched = Object.keys(touched).length > 0;

  const handleChange = (e: React.ChangeEvent<any>) => {
    formik.handleChange(e);
    validateField(e.target.name);
  };

  return {
    ...formik,
    values,
    hasVisibleErrors,
    isFormTouched,
    handleSubmit,
    setFieldValue,
    getError,
    statusHandlers,
    brandingHandlers,
    heroHandlers,
    aboutUsHandlers,
    contactUsHandlers,
    seoHandlers,
    bannersHandlers,
    faviconHandlers,
    footerCustomLinksHandlers,
    homepageSectionsHandlers,
    handleChange,
  };
};
